import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteSessions,
  getCourseSessions,
  setSelectedSessionData,
} from "../../redux/slices/session.slice";
import { RootState, useAppDispatch } from "../../redux/store";
import SearchWithFilter from "../../components/SearchWithFilter";
import SpinnerLoader from "../../components/SpinnerLoader";
import { setSelectedCourseSessionData } from "../../redux/slices/allocation.slice";
import BackButton from "../../components/BackButton";
import EventDetailsModal from "./components/EventDetailsModal";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/LanguageSelector";
import { downloadDescriptionFile } from "../../redux/slices/learner.applications.slice";
import { showToast } from "../../utils/showToast";
import TabNavbar from "../../components/Tabbar";
import { ADMIN_TABS } from "../../utils/TabbarData";
import { MdDelete } from "react-icons/md";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import ErrorModal from "../../components/modal/ErrorModal";

function Sessions() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [modalData, setModalData] = useState<any[]>([]); // Modal data for events
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Delete modal state
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    null
  ); // Selected session ID for deletion
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [currentSessionName, setCurrentSessionName] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const { sessionsData, courseSessionResp, selectedCourseData, loading } =
    useSelector((state: RootState) => state.sessions) as any;

  const { pathname } = useLocation();

  // Handle search input
  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchTerm(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleSeatAllocationNavigation = (id: number, item: any) => {
    const courseSessionById = sessionsData.find(
      (session: any) => session.session_id === id
    );

    dispatch(setSelectedSessionData(item));

    if (!courseSessionById) return;

    dispatch(setSelectedCourseSessionData(courseSessionById));

    navigate(`${pathname}/seat-allocation/${id}`);
  };

  useEffect(() => {
    const id = pathname.split("/")[3];

    if (id) {
      dispatch(
        getCourseSessions({
          course_id: parseInt(id),
          user_id: sessionStorage.getItem("userId"),
          page: currentPage,
          role: "admin",
          page_size: itemsPerPage,
          filter_value: searchTerm, // Pass search term to API
        })
      );
    }
  }, [pathname, currentPage, searchTerm, dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Rendered data (from API)
  const totalPages = Math.ceil(
    (courseSessionResp?.total_item_count || 0) / itemsPerPage
  );

  // Helper function to parse events and extract start/end dates
  const parseEventsForDates = (events: string) => {
    if (!events) return { startDate: null, endDate: null };

    const eventBlocks = events.split(/;\s*/);
    const parsedEvents = eventBlocks
      .map((block) => {
        const [eventName, detailsBlock] = block.split("{(");
        const details = detailsBlock
          ?.replace(")}", "")
          ?.split(",")
          .map((item) => item.trim());

        const [date, time, mode, location, classroom, ...instructorsBlock] =
          details || [];

        const instructors = instructorsBlock
          .join(", ")
          .replace("Instructors:", "")
          .trim();

        return {
          eventName: eventName?.trim(),
          date: date || null,
          time: time || null,
          mode: mode || null,
          location: location || null,
          classroom: classroom || null,
          instructors: instructors || null,
        };
      })
      .filter((event) => event.date && event.time); // Filter out events without date or time

    if (parsedEvents.length === 0) return { startDate: null, endDate: null };

    if (parsedEvents.length === 1) {
      // Single event: start date = event date + first part of time, end date = event date + second part of time
      const event = parsedEvents[0];
      const timeParts = event.time?.split("-").map((t) => t.trim()) || [];
      const startTime = timeParts[0]?.slice(0, 5) || "";
      const endTime = timeParts[1]?.slice(0, 5) || "";

      return {
        startDate: `${event.date} ${startTime}`,
        endDate: `${event.date} ${endTime}`,
      };
    } else {
      // Multiple events: start date = first event date + first part of first event time, end date = last event date + second part of last event time
      const firstEvent = parsedEvents[0];
      const lastEvent = parsedEvents[parsedEvents.length - 1];

      const firstTimeParts =
        firstEvent.time?.split("-").map((t) => t.trim()) || [];
      const lastTimeParts =
        lastEvent.time?.split("-").map((t) => t.trim()) || [];

      const startTime = firstTimeParts[0]?.slice(0, 5) || "";
      const endTime = lastTimeParts[1]?.slice(0, 5) || "";

      return {
        startDate: `${firstEvent.date} ${startTime}`,
        endDate: `${lastEvent.date} ${endTime}`,
      };
    }
  };

  // Parse and set modal data for events
  const handleOpenModal = (events: string) => {
    // Step 1: Split by "," only outside of "({ })"
    // const eventBlocks = events.split(/(?<=\)}),/); // Match ", " only outside the "({ })"
    const eventBlocks = events.split(/;\s*/);
    console.log(eventBlocks);
    const parsedEvents = eventBlocks.map((block) => {
      // Step 2: Split by "({"
      const [eventName, detailsBlock] = block.split("{(");

      // Step 3: Remove closing ")}" and split details inside "({ })" by ","
      const details = detailsBlock
        ?.replace(")}", "")
        ?.split(",")
        .map((item) => item.trim());

      // Step 4: Map details to fields
      const [date, time, mode, location, classroom, ...instructorsBlock] =
        details || [];

      console.log(instructorsBlock);
      // Step 5: Clean instructors
      // Step 5: Extract and clean instructors
      const instructors = instructorsBlock
        .join(", ") // Join remaining parts to recreate full instructors string
        .replace("Instructors:", "") // Remove the "Instructors:" prefix
        .trim();

      return {
        eventName: eventName?.trim(),
        date: date || null,
        time: time
          ? time
              .split("-")
              .map((t) => t.trim().slice(0, 5))
              .join("-")
          : null,
        mode: mode || null,
        location: location || null,
        classroom: classroom || null,
        instructors: instructors || null,
      };
    });

    setModalData(parsedEvents);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);
  const handleDownloadDescriptionFile = async () => {
    setIsDownloading(true);
    try {
      const response = await dispatch(
        downloadDescriptionFile({ course_id: selectedCourseData?.course_id })
      );
      if (response.payload && response.payload.demo_file) {
        const fileUrl = response.payload.demo_file.startsWith('//') 
          ? `https:${response.payload.demo_file}` 
          : response.payload.demo_file;
        window.open(fileUrl, "_blank");
      } else {
        showToast(t("No download material for this course"), "info");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const handleDeleteSession = async (sessionId: number) => {
    try {
      await dispatch(
        deleteSessions({
          session_id: sessionId,
          user_id: sessionStorage.getItem("userId"),
        })
      );

      setIsDeleteModalOpen(false);
      const id = pathname.split("/")[3];
      if (id) {
        await dispatch(
          getCourseSessions({
            course_id: parseInt(id),
            user_id: sessionStorage.getItem("userId"),
            role: "admin",
            page: currentPage,
            page_size: itemsPerPage,
            filter_value: searchTerm, // Pass search term to API
          })
        );
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      setIsErrorModalOpen(true);
      return;
    }
  };

  const handleOpenDeleteSessionModal = (sessionId: number) => {
    setSelectedSessionId(sessionId);
    setIsDeleteModalOpen(true);
  };

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start justify-start gap-10">
      {isMobileOrTablet ? (
        <>
          <TabNavbar
            id=""
            tabs={ADMIN_TABS}
            selectedTabId={1}
            type="Seat Allocation"
          />
          <BackButton to="/courses" state={{ from: "AdminCourseSessions" }} />
        </>
      ) : (
        <>
          <BackButton to="/courses" state={{ from: "AdminCourseSessions" }} />
        </>
      )}
      {Object.keys(selectedCourseData).length > 0 && (
        <div className="w-full">
          <div className="flex flex-row w-full justify-between items-center group cursor-pointer">
            <div className="flex flex-col w-full mb-4">
              <p className="text-2xl font-semibold text-[#961e38]">
                {selectedCourseData.course_name}
              </p>
              <div className="flex flex-wrap items-center justify-start gap-5 pt-2">
                <p className="text-xs font-semibold flex items-center justify-start gap-2 w-full sm:w-auto">
                  <span className="text-[#b5b5b5]">{t("Type")}:</span>
                  <span>
                    {selectedCourseData.category_type
                      ? selectedCourseData.category_type
                      : t("N/A")}
                  </span>
                </p>

                <p className="text-xs font-semibold flex items-center justify-start gap-2 w-full sm:w-auto">
                  <span className="text-[#b5b5b5]">{t("Lang")}:</span>
                  <span>
                    {selectedCourseData.training_languages_oc
                      ? selectedCourseData.training_languages_oc
                      : t("N/A")}
                  </span>
                </p>

                {selectedCourseData.creation_date && (
                  <p className="text-xs font-semibold flex items-center justify-start gap-2 w-full sm:w-auto">
                    <span className="text-[#b5b5b5]">
                      {t("Creation Date")}:
                    </span>
                    <span>{selectedCourseData.creation_date}</span>
                  </p>
                )}
                {selectedCourseData.classroom_training_cost && (
                  <p className="text-xs font-semibold flex items-center justify-start gap-2 w-full sm:w-auto">
                    <span className="text-[#b5b5b5]">
                      {t("Classroom Training Cost")}:
                    </span>
                    <span>{selectedCourseData.classroom_training_cost}</span>
                  </p>
                )}
                {selectedCourseData.online_training_cost && (
                  <p className="text-xs font-semibold flex items-center justify-start gap-2 w-full sm:w-auto">
                    <span className="text-[#b5b5b5]">
                      {t("Online Training Cost")}:
                    </span>
                    <span>{selectedCourseData.online_training_cost}</span>
                  </p>
                )}
              </div>
            </div>
            {!isMobileOrTablet && <LanguageSelector />}
          </div>
          <div className="flex items-center w-full gap-5 mt-2">
            <button
              className="py-1.5 px-5 bg-white uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "#961e38", border: "1px solid #961e38" }}
              onClick={handleDownloadDescriptionFile}
              disabled={isDownloading}
            >
              {isDownloading ? t("Downloading...") : t("Download Description")}
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between w-full gap-5">
        <div className="w-full">
          <SearchWithFilter
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange} // Pass setSearchTerm directly as the handler
            disabled={loading}
          />
        </div>
      </div>

      <div className="block md:hidden w-full">
        {loading ? (
          <div className="flex items-center w-full justify-center mt-4 mb-4">
            <SpinnerLoader />
          </div>
        ) : sessionsData?.length > 0 ? (
          sessionsData.map((item: any, i: any) => {
            return (
              <div
                key={i}
                className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
              >
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Session Name")}</h3>
                  <p className="text-lg">{item.session_name}</p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Start Date")}</h3>
                  <p className="text-lg">
                    {item.events
                      ? parseEventsForDates(item.events).startDate ||
                        item.date_start
                      : item.date_start}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("End Date")}</h3>
                  <p className="text-lg">
                    {item.events
                      ? parseEventsForDates(item.events).endDate ||
                        item.date_end
                      : item.date_end}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Session Details")}</h3>
                  <p className="text-lg">
                    {item.events ? (
                      <span
                        className="text-blue-500 cursor-pointer underline"
                        onClick={() => {
                          setCurrentSessionName(item.session_name);
                          handleOpenModal(item.events);
                        }}
                      >
                        {t("Open")}
                      </span>
                    ) : (
                      t("N/A")
                    )}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Instructors")}</h3>
                  <p className="text-lg">
                    {item.instructors !== " "
                      ? item.instructors
                          .split(",")
                          .map((instructor: any, index: any) => (
                            <span key={index}>
                              {`${instructor.trim()},`}
                              <br />
                            </span>
                          ))
                      : t("N/A")}
                  </p>
                </div>
                <div className="px-4 border-b border-[#c6c6c6] pb-2">
                  <h3 className="font-[600] pb-2">{t("Seat Allocation")}</h3>
                  {item.session_status !== "EXCLUDED_AT_CREATION" && (
                    <div
                      className="py-2 px-4 cursor-pointer font-semibold bg-[#961e38] text-white text-center"
                      onClick={() => {
                        handleSeatAllocationNavigation(item.session_id, item);
                      }}
                    >
                      {t("Open Seat Allocation")}
                    </div>
                  )}
                </div>
                {item.session_status !== "IN_PROGRESS" &&
                  item.session_status !== "ENDED" && (
                    <div className="px-4 border-b border-[#c6c6c6] pb-2">
                      <h3 className="font-[600] pb-2">{t("Actions")}</h3>
                      <button
                        onClick={() =>
                          handleOpenDeleteSessionModal(item.session_id)
                        }
                      >
                        <MdDelete className="text-red-600 text-2xl" />
                      </button>
                    </div>
                  )}
              </div>
            );
          })
        ) : (
          <p className="w-full text-center py-5">
            {t("No Session Data found for")} {selectedCourseData.course_name}
          </p>
        )}

        <EventDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalData={modalData}
          modalName="Session Details"
          sessionName={currentSessionName}
        />
      </div>

      <div className="hidden md:block h-full w-full">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/3 text-center">
                {t("Session Name")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 text-center">
                {t("Start Date")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 text-center">
                {t("End Date")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 text-center">
                {t("Session Details")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 text-center">
                {t("Instructors")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/3 text-center">
                {t("Seat Allocation")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/3 text-center">
                {t("Actions")}
              </th>
              {/* <th className="border-b border-[#961e38] py-2 px-4 w-1/3 text-center">
                Session Status
              </th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  <div className="flex items-center w-full justify-center mt-4 mb-4">
                    <SpinnerLoader />
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {sessionsData.length > 0 ? (
                  <>
                    {sessionsData.map((item: any, i: any) => (
                      <tr key={i}>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/3">
                          {item.session_name}
                        </td>

                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/3">
                          {item.events
                            ? parseEventsForDates(item.events).startDate ||
                              item.date_start
                            : item.date_start}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/3">
                          {item.events
                            ? parseEventsForDates(item.events).endDate ||
                              item.date_end
                            : item.date_end}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                          {item.events ? (
                            <span
                              className="text-blue-500 cursor-pointer underline"
                              onClick={() => {
                                setCurrentSessionName(item.session_name);
                                handleOpenModal(item.events);
                              }}
                            >
                              {t("Open")}
                            </span>
                          ) : (
                            t("N/A")
                          )}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center">
                          {item.instructors !== " "
                            ? item.instructors
                                .split(",")
                                .map((instructor: any, index: any) => (
                                  <span key={index}>
                                    {`${instructor.trim()},`}
                                    <br />
                                  </span>
                                ))
                            : t("N/A")}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/3">
                          {item.session_status !== "EXCLUDED_AT_CREATION" &&
                            item.session_status !== "TO_DELETE" && (
                              <div
                                className="py-2 px-4 cursor-pointer font-semibold bg-[#961e38] text-white text-center"
                                onClick={() => {
                                  handleSeatAllocationNavigation(
                                    item.session_id,
                                    item
                                  );
                                }}
                              >
                                {t("Open Seat Allocation")}
                              </div>
                            )}
                        </td>
                        <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/3">
                          {item.session_status === "TO_DELETE" ? (
                            <h1>{t("Session will be deleted shortly")}</h1>
                          ) : item.session_status !== "IN_PROGRESS" &&
                            item.session_status !== "ENDED" ? (
                            <button
                              onClick={() =>
                                handleOpenDeleteSessionModal(item.session_id)
                              }
                            >
                              <MdDelete className="text-red-600 text-2xl" />
                            </button>
                          ) : (
                            t("N/A")
                          )}
                        </td>
                      </tr>
                    ))}
                  </>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center">
                      {t("No Session Data found for")}{" "}
                      {selectedCourseData.course_name}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
          <EventDetailsModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            modalData={modalData}
            sessionName={currentSessionName}
            modalName="Session Details"
          />
        </table>
      </div>

      {sessionsData.length > 0 && !loading && (
        <div className="flex flex-col md:flex-row justify-between items-center w-full gap-10">
          <div className="flex md:flex-row justify-center items-center gap-4 mt-4">
            {/* Previous Button */}
            <button
              className={`px-4 py-2 border rounded ${
                currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t("Previous")}
            </button>

            {/* Dynamic Page Buttons */}
            {Array.from({
              length: Math.min(totalPages, isMobileOrTablet ? 2 : 4),
            }).map((_, index) => {
              const startPage = Math.max(
                1,
                Math.min(currentPage - 1, totalPages - 3)
              );
              const pageNumber = startPage + index;

              return (
                <button
                  key={pageNumber}
                  className={`px-4 py-2 border rounded ${
                    currentPage === pageNumber
                      ? "bg-[#961e38] text-white"
                      : "bg-white text-[#961e38]"
                  }`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}

            {/* Next Button */}
            <button
              className={`px-4 py-2 border rounded ${
                currentPage === totalPages
                  ? "cursor-not-allowed opacity-50"
                  : ""
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t("Next")}
            </button>
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center gap-5 mt-4 md:mt-0">
            {t("Showing")}{" "}
            {/* {Math.min(
                      itemsPerPage * currentPage,
                      courseResp?.total_item_count || 0
                    )}{" "} */}
            {itemsPerPage * (currentPage - 1) === 0
              ? courseSessionResp?.total_item_count > 0
                ? 1
                : 0
              : itemsPerPage * (currentPage - 1) + 1}{" "}
            -{" "}
            {Math.min(
              itemsPerPage * currentPage,
              courseSessionResp?.total_item_count || 0
            )}{" "}
            {t("of")} {courseSessionResp?.total_item_count || 0} {t("results")}
            <div className="flex items-center gap-3">
              <p className="text-sm font-semibold">{t("Items per page")}:</p>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border rounded p-1"
              >
                <option value={4}>4</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmModal
          closeModal={() => setIsDeleteModalOpen(false)}
          handleDelete={() => {
            if (selectedSessionId !== null) {
              handleDeleteSession(selectedSessionId);
            }
          }}
          loading={loading}
          msg="Are you sure you want to delete this session? This action cannot be undone."
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          closeModal={() => setIsErrorModalOpen(false)}
          modalMessage={
            "An error occurred while deleting the session. Please try again."
          }
        />
      )}
    </div>
  );
}

export default Sessions;
